import { Box, ListItemDecorator, Stack, TabList, Tabs } from "@mui/joy";
import { Outlet, useChildMatches } from "@tanstack/react-router";
import { useLocalStorage } from "react-use";

import Warning from "@/assets/icons/Warning";
import { useLeagueQuery } from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { TabLink } from "../Links";

const useTabFromPath = <TTabList extends string[]>({
  basePath,
  tabs,
  defaultTab,
}: {
  basePath: string;
  tabs: TTabList;
  defaultTab: TTabList[number];
}): TTabList[number] => {
  const tab = useChildMatches({
    select: (matches) => {
      return matches[0]?.routeId.replace(basePath + "/", "");
    },
  });
  return tab && tabs.includes(tab) ? tab : defaultTab;
};

export default function LeagueProfile({
  leagueUuid,
  children,
}: {
  leagueUuid?: string;
  children?: React.ReactNode;
}) {
  const { data: league } = useLeagueQuery(leagueUuid);

  const [hideCategoriesWarning, _] = useLocalStorage(
    `leagues:${league?.uuid}:hideCategoriesWarning`,
    true,
  );

  const hasEmptyCategories = league && league.categories?.length === 0;

  const tab = useTabFromPath({
    basePath: "/gym/$venueUuid/events/series/$leagueUuid/profile",
    tabs: ["details", "categories", "scoring"] as const,
    defaultTab: "details",
  });

  useCurrentVenueUuid();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: "neutral.200",
        padding: 1.5,
      }}
    >
      <Stack
        sx={{
          backgroundColor: "white",
          borderRadius: 8,
          overflow: "hidden",
          flex: 1,
          maxWidth: 800,
        }}
      >
        <Tabs
          value={tab}
          sx={{
            paddingX: 1.5,
            paddingTop: 0.5,
            ".MuiTab-root": {
              flex: 1,
            },
          }}
        >
          <TabList>
            <TabLink
              value="details"
              to={leagueUuid ? "/gym/$venueUuid/events/series/$leagueUuid/profile/details" : "."}
              replace
            >
              Details
            </TabLink>
            <TabLink
              value="categories"
              from="/gym/$venueUuid/events/series/$leagueUuid/profile"
              to="./categories"
              replace
              disabled={!leagueUuid}
            >
              {hasEmptyCategories && !hideCategoriesWarning ? (
                <ListItemDecorator>
                  <Warning />
                </ListItemDecorator>
              ) : null}
              Categories
            </TabLink>
            <TabLink
              value="scoring"
              from="/gym/$venueUuid/events/series/$leagueUuid/profile"
              to="./scoring"
              replace
              disabled={!leagueUuid}
            >
              Scoring
            </TabLink>
          </TabList>
        </Tabs>
        {children || <Outlet />}
      </Stack>
    </Box>
  );
}
